import {zodResolver} from '@hookform/resolvers/zod';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useForm} from 'react-hook-form';

import {GetAddressByZipCodeResponse, PrincipalFavoritePlaceEnum} from 'domain/usecases';
import {FeaturesEnum, OperationStateTypesEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useGetAddressByZipCodeQuery} from 'presentation/pages/private/profile/queries/useGetAddressByZipCodeQuery';
import {useChangeDataAlertStore} from 'presentation/pages/private/profile/store/changeDataAlertStore';
import {AlertEnum} from 'presentation/pages/private/profile/store/changeDataAlertStore/types';
import {useChangeDataFormStateStore} from 'presentation/pages/private/profile/store/changeDataFormStateStore';
import {translations} from 'presentation/translations-new';
import {formatMasks, formatText} from 'presentation/utils';

const {cepNotFound} = translations['pt-br'].privateFlow.profile.changeData.addressErrors;

import {WarningInfo} from '../AddressForm/types';
import {AddressDataFormValues, makeAddressDataSchema} from '../AddressForm/validation';
import {FieldDisabledState, UseAddressFormProps} from './types';

const HOUSE_LABEL = 'Casa';

const isPrincipalFavoriteAddress = (flPrincipal: unknown) =>
	flPrincipal === true || flPrincipal === PrincipalFavoritePlaceEnum.YES;

function useAddressForm({
	onCancel,
	address,
	hasFavoriteAddressField,
	forceMainAddress,
	favoriteAddressDescription,
	enableCepNotFoundError = false,
}: UseAddressFormProps) {
	const schema = useMemo(
		() => makeAddressDataSchema(hasFavoriteAddressField),
		[hasFavoriteAddressField],
	);

	const derivedIsMainAddress = useMemo(() => {
		if (!hasFavoriteAddressField) return false;
		if (forceMainAddress) return true;
		if (!address) return false;
		const flPrincipal = (address as Record<string, unknown>)?.flPrincipal;
		return isPrincipalFavoriteAddress(flPrincipal);
	}, [address, forceMainAddress, hasFavoriteAddressField]);
	const [isMainAddress, setIsMainAddress] = useState(derivedIsMainAddress);
	const isMainAddressDisabled = useMemo(() => {
		if (!hasFavoriteAddressField) return false;
		if (forceMainAddress) return true;
		if (!address) return false;
		const flPrincipal = (address as Record<string, unknown>)?.flPrincipal;
		return isPrincipalFavoriteAddress(flPrincipal);
	}, [address, forceMainAddress, hasFavoriteAddressField]);
	const [isDiscardChangesModalOpenState, setIsDiscardChangesModalOpenState] = useState(false);
	const setChangeDataFormIsDirtyState = useChangeDataFormStateStore(
		state => state.updateFormState,
	);
	const resetChangeDataAlertState = useChangeDataAlertStore(state => state.reset);
	const changeDataAlertState = useChangeDataAlertStore(state => state.status);
	const {isEnabled: isNewProfileFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.ADD__PORTAL_FEATURE_NEW_PROFILE,
	);

	const [showOtherFields, setShowOtherFields] = useState(false);
	const [operationState, setOperationState] = useState<OperationStateTypesEnum>(
		OperationStateTypesEnum.NONE,
	);
	const [fieldDisabledState, setFieldDisabledState] = useState<FieldDisabledState>({
		address: true,
		district: true,
		state: true,
		city: true,
	});
	const [warningInfo, setWarningInfo] = useState<WarningInfo>(null);
	const skipNextFetchRef = useRef(false);

	const showAlert =
		changeDataAlertState.status && changeDataAlertState.type === AlertEnum.ADDRESS;
	const {typeMessage} = changeDataAlertState;

	const methods = useForm<AddressDataFormValues>({
		resolver: zodResolver(schema),
		mode: 'onSubmit',
		defaultValues: {
			favoriteAddress:
				hasFavoriteAddressField && forceMainAddress
					? HOUSE_LABEL
					: favoriteAddressDescription,
		},
	});

	const {
		register,
		formState: {errors, isDirty},
		watch,
		setValue,
		handleSubmit,
		clearErrors,
	} = methods;

	const watchZipCode = watch('zipCode');
	const watchWithoutNumber = watch('withoutNumber');
	const unmaskedZipCode = formatMasks('clear', watchZipCode);

	const {refetch, data: addressCachedData} = useGetAddressByZipCodeQuery(unmaskedZipCode);

	const handleBack = () => {
		if (!isNewProfileFeatureFlagEnabled && isDirty) {
			setIsDiscardChangesModalOpenState(true);
			return;
		}

		onCancel();
	};

	const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
		setValue('withoutNumber', e.target.checked);
		if (e.target.checked) {
			setValue('number', '');
		}
	};

	const fillAddressFields = useCallback(
		(addressData?: GetAddressByZipCodeResponse, resetNumber = false) => {
			clearErrors('zipCode');
			if (addressData?.nmUf) {
				setValue('state', addressData.nmUf.toUpperCase());
				setFieldDisabledState(prev => ({...prev, state: true}));
			} else {
				setValue('state', '');
				setFieldDisabledState(prev => ({...prev, state: false}));
			}
			if (addressData?.nmCidade) {
				setValue('city', formatText(addressData.nmCidade));
				setFieldDisabledState(prev => ({...prev, city: true}));
			} else {
				setValue('city', '');
				setFieldDisabledState(prev => ({...prev, city: false}));
			}
			if (addressData?.nmRua) {
				const addressValue = addressData.nmTipoLogradouro
					? `${formatText(addressData.nmTipoLogradouro)}. ${formatText(addressData.nmRua)}`
					: formatText(addressData.nmRua);
				setValue('address', addressValue);
				setFieldDisabledState(prev => ({...prev, address: true}));
			} else {
				setValue('address', '');
				setFieldDisabledState(prev => ({...prev, address: false}));
			}
			if (addressData?.nmBairro) {
				setValue('district', formatText(addressData?.nmBairro));
				setFieldDisabledState(prev => ({...prev, district: true}));
			} else {
				setValue('district', '');
				setFieldDisabledState(prev => ({...prev, district: false}));
			}

			setValue('complement', '');
			setValue('number', '');
			setValue('withoutNumber', false);

			if (address && !resetNumber) {
				setValue('complement', address?.dsComplemento ?? '');
				setValue('number', address?.nuEndereco?.toString() ?? '');
			}

			setOperationState(OperationStateTypesEnum.DONE);
			setShowOtherFields(true);
		},
		[address, setValue, clearErrors],
	);

	const getAddressByZipCode = async () => {
		const addressStreetLower = address?.nmRua?.toLocaleLowerCase();
		if (addressCachedData) {
			const resetNumber = !addressStreetLower?.includes(
				addressCachedData.nmRua.toLocaleLowerCase(),
			);
			fillAddressFields(addressCachedData, resetNumber);
			return;
		}

		setOperationState(OperationStateTypesEnum.PENDING);

		const {data: addressResponse, error} = await refetch();

		if (error) {
			setOperationState(OperationStateTypesEnum.ERROR);
			setValue('state', '');
			setValue('city', '');
			setValue('address', '');
			setValue('district', '');
			setValue('complement', '');
			setValue('number', '');
			setValue('withoutNumber', false);
			setFieldDisabledState({
				address: false,
				district: false,
				state: false,
				city: false,
			});
			setShowOtherFields(true);
			setWarningInfo('apiError');

			return;
		}

		const allFieldsEmpty =
			!addressResponse || Object.values(addressResponse).every(value => !value);

		if (allFieldsEmpty && enableCepNotFoundError) {
			setOperationState(OperationStateTypesEnum.DONE);
			setShowOtherFields(false);
			methods.setError('zipCode', {
				type: 'manual',
				message: cepNotFound,
			});
			return;
		}

		if (allFieldsEmpty) {
			setWarningInfo('allFieldsEmpty');
		}

		fillAddressFields(
			addressResponse,
			!addressStreetLower?.includes(addressResponse?.nmRua?.toLocaleLowerCase() ?? ''),
		);
	};

	const handleChange = useCallback(
		(value: string, shouldDirty = true) => {
			setValue('zipCode', value, {shouldDirty});
		},
		[setValue],
	);

	const skipNextZipCodeFetch = useCallback(() => {
		skipNextFetchRef.current = true;
	}, []);

	const clearWarningInfo = useCallback(() => {
		setWarningInfo(null);
	}, []);

	const resetFormFields = useCallback(() => {
		setValue('zipCode', '');
		setValue('state', '');
		setValue('city', '');
		setValue('address', '');
		setValue('district', '');
		setValue('complement', '');
		setValue('number', '');
		setValue('withoutNumber', false);
		setFieldDisabledState({
			address: true,
			district: true,
			state: true,
			city: true,
		});
		setShowOtherFields(false);
		setOperationState(OperationStateTypesEnum.NONE);
		setWarningInfo(null);
		clearErrors();
	}, [clearErrors, setValue]);

	useEffect(() => {
		setIsMainAddress(derivedIsMainAddress);
	}, [derivedIsMainAddress]);

	useEffect(() => {
		setChangeDataFormIsDirtyState({
			isFormDirty: isDirty,
		});
	}, [isDirty]);

	useEffect(() => {
		if (isNewProfileFeatureFlagEnabled && showAlert) {
			resetChangeDataAlertState();
		}

		if (unmaskedZipCode.length === 8) {
			if (skipNextFetchRef.current) {
				skipNextFetchRef.current = false;
				return;
			}
			getAddressByZipCode();
			return;
		}

		if (unmaskedZipCode.length !== 8) {
			setShowOtherFields(false);
			setWarningInfo(null);
			clearErrors();
		}
	}, [watchZipCode]);

	useEffect(() => {
		if (!address?.nuCep) {
			return;
		}
		skipNextFetchRef.current = true;
		handleChange(address.nuCep, false);
		fillAddressFields(address);
	}, []);

	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const zipCode = urlParams.get('cep');
		if (!zipCode) {
			return;
		}
		handleChange(zipCode);
	}, []);

	return {
		methods,
		showOtherFields,
		operationState,
		fieldDisabledState,
		isMainAddress,
		isMainAddressDisabled,
		setIsMainAddress,
		warningInfo,
		unmaskedZipCode,
		watchWithoutNumber,
		showAlert,
		typeMessage,
		isNewProfileFeatureFlagEnabled,
		isDiscardChangesModalOpenState,
		setIsDiscardChangesModalOpenState,
		handleBack,
		handleCheckbox,
		handleChange,
		handleSubmit,
		fillAddressFields,
		skipNextZipCodeFetch,
		clearWarningInfo,
		resetFormFields,
		register,
		errors,
	};
}

export {useAddressForm};
